import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import { useUser } from "../UserContext";
import SpotlightRunner from "./SpotlightRunner";
import SpotlightCompletionCard from "./SpotlightCompletionCard";
import { SpotlightResumeBanner } from "./SpotlightCard";
import { getChapter, getFirstChapter } from "./tutorialRegistry";
import {
	COMPLETE_CHAPTER,
	getChapterNavigation,
	isChapterRoute,
	COMPLETE_TUTORIAL,
	getChapterProgress,
	getFirstStepId,
	getNextStepId,
	getStep,
	isRequirementMet,
	resolveTransition,
} from "./tutorialEngine";
import { getSpotlightSignals, subscribeToSpotlightEvents, subscribeToSpotlightSignals } from "./spotlightBus";
import { ensureSpotlightStyles } from "./spotlightTheme";
import {
	TUTORIAL_STATUS,
	createStartedTutorialState,
	saveTutorialState,
	withTutorialFinished,
	withTutorialPosition,
} from "../../services/SpotlightTutorialService";

const PREVIEW_STORAGE_KEY = "mytassel.spotlightTutorialPreview";
const CHAPTER_NAVIGATION_DELAY_MS = 1200;
const CHAPTER_NAVIGATION_TIMEOUT_MS = 5000;
// Where a user with a pending tutorial is picked up: straight away on My Profile, or from the Hub they land on after login.
const TUTORIAL_LAUNCH_ROUTES = ["Profile", "Hub"];

const inactiveContext = {
	isActive: false,
	isPreview: false,
	startTutorial: () => {},
	startPreview: () => {},
	exitPreview: () => {},
};

const SpotlightTutorialContext = createContext(inactiveContext);

export const useSpotlightTutorial = () => useContext(SpotlightTutorialContext);

const readStoredPreview = () => {
	try {
		const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(PREVIEW_STORAGE_KEY) : null;
		return raw ? JSON.parse(raw) : null;
	} catch (error) {
		return null;
	}
};

const writeStoredPreview = (state) => {
	if (typeof sessionStorage === "undefined") return;
	if (state) sessionStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(state));
	else sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
};

const toArray = (value) => (value ? (Array.isArray(value) ? value : [value]) : []);

const uniqueAppend = (list = [], value) => (list.includes(value) ? list : [...list, value]);

/**
 * Owns tutorial progress and orchestrates it: persistence (Firestore, mirrored into UserContext),
 * resuming after refresh/navigation, reacting to user events, chapter hand-offs and admin preview.
 * Rendering of the dimmed overlay/card lives in SpotlightRunner.
 */
export const SpotlightTutorialProvider = ({ children }) => {
	const navigation = useNavigation();
	const { currentUser, currentUserData, setCurrentUserData, authLoading } = useUser();

	const [previewState, setPreviewState] = useState(readStoredPreview);
	const [routeName, setRouteName] = useState(null);
	const [pendingChapter, setPendingChapter] = useState(null);
	const [completionStep, setCompletionStep] = useState(null);
	const [, bumpSignalVersion] = useReducer((version) => version + 1, 0);
	const hasAutoNavigatedRef = useRef(false);

	const isPreview = Boolean(previewState);
	const tutorialState = isPreview ? previewState : currentUserData?.spotlightTutorial;
	const isRunning = tutorialState?.status === TUTORIAL_STATUS.IN_PROGRESS;
	const chapter = isRunning ? getChapter(tutorialState.currentChapterId) : null;
	const step = chapter ? getStep(chapter, tutorialState.currentStepId) : null;

	const isUserReady = !authLoading && Boolean(currentUser) && Boolean(currentUserData);

	const latestRef = useRef({});
	latestRef.current = { tutorialState, chapter, step, isPreview, currentUserData, email: currentUserData?.email };

	useEffect(() => {
		ensureSpotlightStyles();
	}, []);

	//#region - Route tracking
	const syncRoute = useCallback(() => {
		if (navigation?.isReady?.() === false) return;
		setRouteName(navigation?.getCurrentRoute?.()?.name || null);
	}, [navigation]);

	useEffect(() => {
		syncRoute();
		return navigation?.addListener?.("state", syncRoute);
	}, [navigation, syncRoute, isRunning]);

	const navigateTo = useCallback(
		(chapterToOpen) => {
			if (navigation?.isReady?.() === false) return;
			const { name, params } = getChapterNavigation(chapterToOpen, latestRef.current.currentUserData);
			navigation.navigate(name, params);
		},
		[navigation]
	);
	//#endregion

	//#region - Persistence
	const persistState = useCallback(
		(nextState) => {
			latestRef.current.tutorialState = nextState;
			if (latestRef.current.isPreview) {
				setPreviewState(nextState);
				writeStoredPreview(nextState);
				return;
			}
			setCurrentUserData((previous) => (previous ? { ...previous, spotlightTutorial: nextState } : previous));
			saveTutorialState(latestRef.current.email, nextState).catch((error) => console.error("Failed to save tutorial progress:", error));
		},
		[setCurrentUserData]
	);

	const exitPreview = useCallback(() => {
		latestRef.current.isPreview = false;
		setPreviewState(null);
		writeStoredPreview(null);
	}, []);
	//#endregion

	//#region - Step/chapter actions
	const finishTutorial = useCallback(
		(status, extra = {}) => {
			const { tutorialState: state, isPreview: previewing } = latestRef.current;
			if (previewing) {
				exitPreview();
				return;
			}
			persistState(withTutorialFinished(state, status, extra));
		},
		[persistState, exitPreview]
	);

	const goToStep = useCallback(
		(stepId) => {
			const { tutorialState: state, chapter: currentChapter } = latestRef.current;
			persistState(withTutorialPosition(state, { chapterId: currentChapter.id, stepId }));
		},
		[persistState]
	);

	const finishChapter = useCallback(() => {
		const { tutorialState: state, chapter: currentChapter, currentUserData: userData } = latestRef.current;
		const completedChapters = uniqueAppend(state.completedChapters, currentChapter.id);
		const nextChapter = getChapter(currentChapter.next?.({ userData }));

		if (!nextChapter) {
			finishTutorial(TUTORIAL_STATUS.COMPLETED, { completedChapters });
			return;
		}

		persistState(withTutorialPosition(state, { chapterId: nextChapter.id, stepId: getFirstStepId(nextChapter), completedChapters }));
		const shouldFollowToNextChapter = nextChapter.route !== currentChapter.route && nextChapter.followOnHandoff !== false;
		if (shouldFollowToNextChapter) setPendingChapter(nextChapter);
	}, [persistState, finishTutorial]);

	const advance = useCallback(() => {
		const { chapter: currentChapter, step: currentStep } = latestRef.current;
		if (!currentChapter || !currentStep) return;
		currentStep.onAdvance?.();
		const skipTarget = currentStep.skipTo?.({ userData: latestRef.current.currentUserData, getSignals: getSpotlightSignals });
		if (skipTarget === COMPLETE_CHAPTER) {
			finishChapter();
			return;
		}
		const nextStepId = getStep(currentChapter, skipTarget)?.id || getNextStepId(currentChapter, currentStep.id);
		if (nextStepId) goToStep(nextStepId);
		else finishChapter();
	}, [goToStep, finishChapter]);

	const goBack = useCallback(() => {
		const { chapter: currentChapter, step: currentStep } = latestRef.current;
		const index = currentChapter.steps.findIndex((candidate) => candidate.id === currentStep.id);
		const previousStep = currentChapter.steps.slice(0, index).reverse().find((candidate) => !candidate.silent);
		if (previousStep) goToStep(previousStep.id);
	}, [goToStep]);

	const skipTutorial = useCallback(() => {
		const { chapter: currentChapter, step: currentStep } = latestRef.current;
		finishTutorial(TUTORIAL_STATUS.SKIPPED, { skippedAtChapterId: currentChapter.id, skippedAtStepId: currentStep.id });
	}, [finishTutorial]);

	const applyTransition = useCallback(
		(goto) => {
			const { chapter: currentChapter, step: currentStep } = latestRef.current;
			if (goto === COMPLETE_TUTORIAL) finishTutorial(TUTORIAL_STATUS.COMPLETED, { exitedEarlyAtStepId: currentStep.id });
			else if (goto === COMPLETE_CHAPTER) finishChapter();
			else if (goto === "next") advance();
			else if (getStep(currentChapter, goto)) goToStep(goto);
		},
		[advance, goToStep, finishTutorial]
	);

	const handleTargetGone = useCallback(() => {
		const { step: currentStep } = latestRef.current;
		const goto = typeof currentStep?.completeWhenTargetGone === "string" ? currentStep.completeWhenTargetGone : "next";
		applyTransition(goto);
	}, [applyTransition]);

	const handleTrigger = useCallback(
		(trigger) => {
			const { step: currentStep } = latestRef.current;
			if (!currentStep) return;
			const goto = resolveTransition(currentStep, trigger);
			if (goto) applyTransition(goto);
		},
		[applyTransition]
	);
	//#endregion

	//#region - Reacting to the page
	useEffect(() => subscribeToSpotlightEvents(({ name, payload }) => handleTrigger({ type: "event", name, payload })), [handleTrigger]);

	useEffect(() => subscribeToSpotlightSignals(bumpSignalVersion), []);

	useEffect(() => {
		if (routeName && step) handleTrigger({ type: "route", name: routeName });
	}, [routeName, step?.id]);

	useEffect(() => {
		if (!step) return;
		const scopes = new Set(toArray(step.waitFor).filter((transition) => transition.signal).map((transition) => transition.signal.scope));
		scopes.forEach((scope) => handleTrigger({ type: "signal", scope, snapshot: getSpotlightSignals(scope) }));
	});
	//#endregion

	//#region - Self-healing and hand-offs
	useEffect(() => {
		if (isRunning && !step) {
			const fallbackChapter = chapter || getFirstChapter();
			persistState(withTutorialPosition(tutorialState, { chapterId: fallbackChapter.id, stepId: getFirstStepId(fallbackChapter) }));
		}
	}, [isRunning, step]);

	useEffect(() => {
		if (!chapter || isPreview || !currentUserData || !chapter.isEligible) return;
		if (!chapter.isEligible({ userData: currentUserData })) finishTutorial(TUTORIAL_STATUS.COMPLETED, { exitedEarlyAtStepId: step?.id });
	}, [chapter?.id, currentUserData?.accountType]);

	useEffect(() => {
		if (!pendingChapter) return undefined;
		const navigateTimeout = setTimeout(() => navigateTo(pendingChapter), CHAPTER_NAVIGATION_DELAY_MS);
		const releaseTimeout = setTimeout(() => setPendingChapter(null), CHAPTER_NAVIGATION_TIMEOUT_MS);
		return () => {
			clearTimeout(navigateTimeout);
			clearTimeout(releaseTimeout);
		};
	}, [pendingChapter, navigateTo]);

	useEffect(() => {
		if (pendingChapter && isChapterRoute(pendingChapter, routeName)) setPendingChapter(null);
	}, [routeName, pendingChapter]);

	useEffect(() => {
		if (!tutorialState?.autoNavigate) {
			hasAutoNavigatedRef.current = false;
			return;
		}
		const shouldAutoNavigate = isRunning && chapter && routeName && !isChapterRoute(chapter, routeName);
		if (!shouldAutoNavigate || hasAutoNavigatedRef.current) return;
		hasAutoNavigatedRef.current = true;
		navigateTo(chapter);
		persistState({ ...tutorialState, autoNavigate: false });
	}, [isRunning, chapter?.id, tutorialState?.autoNavigate, routeName]);
	//#endregion

	//#region - Public API
	const startTutorial = useCallback(
		({ chapterId } = {}) => {
			const startChapter = getChapter(chapterId) || getFirstChapter();
			const { tutorialState: state } = latestRef.current;
			persistState(createStartedTutorialState({ chapterId: startChapter.id, stepId: getFirstStepId(startChapter), previous: state || {}, extra: { autoNavigate: true } }));
			navigateTo(startChapter);
		},
		[persistState, navigateTo]
	);

	useEffect(() => {
		const isPending = tutorialState?.status === TUTORIAL_STATUS.PENDING;
		const canLaunch = isPending && !isPreview && isUserReady && currentUserData?.referralPending !== true;
		if (canLaunch && TUTORIAL_LAUNCH_ROUTES.includes(routeName)) startTutorial();
	}, [tutorialState?.status, isPreview, isUserReady, currentUserData?.referralPending, routeName, startTutorial]);

	const startPreview = useCallback(
		(chapterId) => {
			const previewChapter = getChapter(chapterId) || getFirstChapter();
			const state = createStartedTutorialState({ chapterId: previewChapter.id, stepId: getFirstStepId(previewChapter), extra: { preview: true } });
			latestRef.current.isPreview = true;
			setPreviewState(state);
			writeStoredPreview(state);
			navigateTo(previewChapter);
		},
		[navigateTo]
	);

	const contextValue = useMemo(
		() => ({ isActive: isRunning, isPreview, startTutorial, startPreview, exitPreview }),
		[isRunning, isPreview, startTutorial, startPreview, exitPreview]
	);
	//#endregion

	const isWaitingForReferral = !isPreview && currentUserData?.referralPending === true;
	const canShow = Boolean(chapter && step) && isUserReady && !isWaitingForReferral && !pendingChapter;
	const isOnChapterRoute = isChapterRoute(chapter, routeName);

	const requirementMet = step ? isRequirementMet(step, getSpotlightSignals) : true;

	// The completion step marks onboarding finished as soon as it appears, so nothing the user does next can leave it unfinished.
	const shouldShowCompletion = canShow && isOnChapterRoute && Boolean(step.completion);
	useEffect(() => {
		if (!shouldShowCompletion) return;
		setCompletionStep(step);
		finishChapter();
	}, [shouldShowCompletion]);

	return (
		<SpotlightTutorialContext.Provider value={contextValue}>
			{children}
			{canShow && isOnChapterRoute && !step.completion && (
				<SpotlightRunner
					chapter={chapter}
					step={step}
					isPreview={isPreview}
					isRequirementMet={requirementMet}
					userData={currentUserData}
					onNext={advance}
					onBack={goBack}
					onSkip={skipTutorial}
					onTargetGone={handleTargetGone}
				/>
			)}
			{completionStep && <SpotlightCompletionCard step={completionStep} userData={currentUserData} onClose={() => setCompletionStep(null)} />}
			{canShow && routeName && !isOnChapterRoute && (
				<SpotlightResumeBanner
					chapterTitle={chapter.title}
					progress={getChapterProgress(chapter, step.id)}
					isPreview={isPreview}
					onResume={() => navigateTo(chapter)}
					onSkip={skipTutorial}
				/>
			)}
		</SpotlightTutorialContext.Provider>
	);
};
